/**
 * Messages between the browsers and the relay server (apps/server, see Protocol.cs). JSON objects with a type `t`.
 * The server never simulates: it stamps every command with a tick and the sender's seat, and says up to which
 * tick the browsers may simulate.
 */
import type { Command, CommandInput, ScenarioDesc } from '@invaders/game';

export interface RoomSettings {
  /** 2 to 8 (MAX_PLAYERS). */
  players: number;
  /** Invaders per player, 1 to 4, 16 at most on the map (maxUnitsPerPlayer). */
  units: number;
  /** Rounds before the end of the world (the lava rises, lava bombs fall from the sky), 3 to 40. */
  lava: number;
  /** A map id (MAP_IDS), or 'random': the host's browser draws one at the start. */
  map: string;
  /** The host's choice of weapons (encodeArsenal), '' for the GDD's. */
  arsenal?: string;
  /**
   * Seconds of a turn's aim (the GDD's 30), friendly fire (on), hidden placement (off; only with the free placement), free
   * placement (on; off: the computer draws every invader and its spot), ghost turns (off): the host's options.
   */
  turn?: number;
  friendlyFire?: boolean;
  hidden?: boolean;
  free?: boolean;
  ghosts?: boolean;
  /** Listed on everyone's « Join » tab (author's addition); else only its code or link lets one in. */
  public?: boolean;
  /** Who may ping the map (author's option): nobody, the players (by default) or the spectators too. */
  pings?: PingRule;
  /**
   * The room of a Discord Activity (author's mode): fixed settings, 8 seats, and each player gets the most invaders the
   * players there may have (maxUnitsPerPlayer at the start; `units` is only the count for a full room).
   */
  discord?: boolean;
}

/** Who may ping in a room: nobody, the seats, or the seats and the spectators. */
export type PingRule = 'none' | 'players' | 'all';

export interface RoomPlayer {
  seat: number;
  name: string;
  ready: boolean;
  connected: boolean;
  /** Excluded by the host during the match: out of it. */
  kicked?: boolean;
}

/** `you` = -1 (and no token) for a spectator; `watchers`: how many spectators the room has. */
export type RoomMessage = {
  t: 'room'; code: string; you: number; token: string | null; host: number;
  state: 'lobby' | 'playing'; settings: RoomSettings; players: RoomPlayer[]; watchers?: number;
};
/**
 * The match from its start: the scenario and every command so far (a browser that comes back, or a spectator with
 * `you` = -1, catches up).
 */
export type StartMessage = { t: 'start'; you: number; names: string[]; scenario: ScenarioDesc; upTo: number; cmds: Command[] };
/** The browsers may simulate every tick before `upTo`; `cmds` are stamped with ticks they have not simulated yet. */
export type FrameMessage = { t: 'frame'; upTo: number; cmds: Command[] };
export type DesyncMessage = { t: 'desync'; tick: number; hashes: { seat: number; hash: number }[] };
export type ErrorMessage = { t: 'error'; message: string };
/** A ping (author's addition): a point of the map from a seat (-1: a spectator), shown to the whole room. */
export type PingMessage = { t: 'ping'; x: number; y: number; seat: number };
/** The host excluded this browser (author's addition): it is no longer in the room. */
export type KickedMessage = { t: 'kicked'; message: string };
/**
 * A public room as the « Join » tab lists it (author's addition): its host's name, the seats taken, whether its match has
 * started (then it can only be watched), its rules.
 */
export interface ListedRoom { code: string; host: string; players: number; state: 'lobby' | 'playing'; watchers: number; settings: RoomSettings }
/** The answer to `list`: the rooms still in their lobby first, then the matches in progress. */
export type RoomsMessage = { t: 'rooms'; rooms: ListedRoom[] };
/** The answer to `find` (Discord, author's addition): the room of the Activity's instance, null before one of its players creates it. */
export type FoundMessage = {
  t: 'found'; instance: string;
  room: { code: string; host: string; state: 'lobby' | 'playing'; players: number; max: number } | null;
};

/**
 * The end of a match as a browser saw it (author's addition, for the admin page): the same in every browser, since they
 * compute the same match. The server keeps the first one and notes whether the others agree; it does not read it.
 */
export interface MatchResult {
  /** Seat of the winner, or DRAW (-2). */
  winner: number;
  round: number;
  turn: number;
  /** Height of the rising lava, in cells (0: it had not started). */
  lava: number;
  /** Every invader, in placement order: its HP at the end, the damage it took, the round it died in (0: alive). */
  units: { player: number; kind: number; hp: number; damage: number; died: number; offMap: boolean }[];
}

/** The answer to `discord`: the player's Discord name, its team name from now on; null if the server could not read it. */
export type DiscordUserMessage = { t: 'discordUser'; name: string | null };

export type ServerMessage = RoomMessage | StartMessage | FrameMessage | DesyncMessage | ErrorMessage | PingMessage | KickedMessage | RoomsMessage | FoundMessage | DiscordUserMessage;

export type ClientMessage =
  /** `code`: the host's last code, kept if nobody uses it now. */
  | {
    t: 'create'; name: string; players: number; units: number; lava: number; map: string; arsenal?: string; turn?: number; friendlyFire?: boolean;
    hidden?: boolean; free?: boolean; ghosts?: boolean; public?: boolean; pings?: PingRule; code?: string;
  }
  /**
   * Discord (author's mode): the room of the Activity's instance, with its fixed settings, opened by the first of its
   * players; the others land in it.
   */
  | { t: 'create'; name: string; instance: string }
  /** Discord: the room of the Activity's instance (one answer per second). */
  | { t: 'find'; instance: string }
  /** Discord: the code of the player's authorization (scope identify); the server reads its Discord name with it. */
  | { t: 'discord'; code: string }
  /** During a match, a newcomer (without the token of a seat) becomes a spectator. */
  | { t: 'join'; code: string; name?: string; token?: string }
  /** A spectator: in the room without a seat (author's addition). */
  | { t: 'watch'; code: string }
  | { t: 'ready'; ready: boolean }
  | { t: 'start'; scenario: ScenarioDesc }
  /** Its tick and player are set by the server. */
  | { t: 'cmd'; cmd: CommandInput }
  | { t: 'hash'; tick: number; hash: number }
  /** Once, at the first tick the match is over. */
  | { t: 'result'; tick: number; result: MatchResult }
  | { t: 'ping'; x: number; y: number }
  /** The host excludes a seat: freed in the lobby, out of the match once it started (the server sends its `forfeit`). */
  | { t: 'kick'; seat: number }
  /** The public rooms (one list per second at most). */
  | { t: 'list' }
  | { t: 'leave' };

/** GDD section 13: fingerprints of the match are compared every 5 s. */
export const HASH_EVERY_TICKS = 300;
